import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

import type { PropType } from 'vue';
import { ref, toRefs, computed, watch } from 'vue';
import { useRoute } from 'vue-router';

// Personal components
import VueSkipToSingle from '@components/vue-skip-to/VueSkipToSingle.vue';
import VueSkipToList from '@components/vue-skip-to/VueSkipToList.vue';
import { programmaticFocus } from './util';
import { ISkipLink, ISkipLinks } from './vue-skip-to.interface';
import { SkipLinkEnum } from '@shared/enum/skip-link.enum';

/**
 * Globals
 */
const defaultSkipLinkListLabel = "Liens d'évitement";

export default /*@__PURE__*/_defineComponent({
  __name: 'VueSkipTo',
  props: {
  listLabel: { required: false, type: String },
  label: { required: false, type: String, default: 'Skip to main content' },
  skipLinks: {
    required: false,
    type: Object as PropType<string | Array<ISkipLink>>,
  },
},
  setup(__props) {

// Vendor
const route = useRoute();

/**
 * Refs
 */
const skipTo = ref<HTMLDivElement>();

/**
 * Properties
 */
const properties = __props;
const { listLabel, label, skipLinks } = toRefs(properties);

/**
 * Datas
 */
const defaultSkipLinks: Array<ISkipLink> = [
  {
    anchor: `#${SkipLinkEnum.NAVIGATION_MAIN}`,
    label: 'Aller au contenu',
    ariaLabel: 'Aller au contenu',
  },
  {
    anchor: `#${SkipLinkEnum.NAVIGATION_MENU}`,
    label: 'Aller au menu',
    ariaLabel: 'Aller au menu',
  },
  {
    anchor: `#${SkipLinkEnum.NAVIGATION_FOOTER}`,
    label: 'Aller au bas de page',
    ariaLabel: 'Aller au bas de page',
  },
];

/**
 * Reactive data
 */
const focused = ref<boolean>(false);

/**
 * Computed properties
 */
const isList = computed<boolean>(
  () =>
    skipLinks?.value == null ||
    (skipLinks?.value != null &&
      Array.isArray(skipLinks?.value)),
);
const comp = computed<typeof VueSkipToList | typeof VueSkipToSingle>(() =>
  isList.value ? VueSkipToList : VueSkipToSingle,
);
const props = computed<ISkipLinks>(() => {
  if (isList.value) {
    return {
      listLabel: listLabel?.value ?? defaultSkipLinkListLabel,
      to: skipLinks?.value ?? defaultSkipLinks,
    } as ISkipLinks;
  } else {
    return {
      label: label.value,
      to: skipLinks?.value ?? `#${SkipLinkEnum.NAVIGATION_MAIN}`,
    } as ISkipLinks;
  }
});

/**
 * Methods
 */
function focusWithin(...args: unknown[]) {
  focused.value = args[0] === true;
}

/**
 * Watch
 */
watch(
  () => route.path,
  () => {
    programmaticFocus(skipTo.value);
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["vue-skip-to", { focused: focused.value }]),
    ref_key: "skipTo",
    ref: skipTo
  }, [
    (_openBlock(), _createBlock(_resolveDynamicComponent(comp.value), _mergeProps({
      onFocused: focusWithin,
      onFocusWithin: focusWithin
    }, props.value), null, 16 /* FULL_PROPS */))
  ], 2 /* CLASS */))
}
}

})